import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "offers";

export function useOfferList(options?: {
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	status?: string | undefined;
	search?: string | undefined;
	offerDateFrom?: string | undefined;
	offerDateTo?: string | undefined;
	emailSent?: boolean | undefined;
	sort?: string | undefined;
	dir?: "asc" | "desc" | undefined;
	page?: number;
	limit?: number;
	enabled?: boolean;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.opportunityId) params.set("opportunityId", options.opportunityId);
			if (options?.assignedTo) params.set("assignedTo", options.assignedTo);
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.offerDateFrom) params.set("offerDateFrom", options.offerDateFrom);
			if (options?.offerDateTo) params.set("offerDateTo", options.offerDateTo);
			if (options?.emailSent !== undefined) params.set("emailSent", String(options.emailSent));
			if (options?.sort) params.set("sort", options.sort);
			if (options?.dir) params.set("dir", options.dir);
			return api.get<{ items: Offer[]; total: number }>(`/api/offers?${params.toString()}`);
		},
		enabled: options?.enabled !== false,
	});
}

export function useOfferDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Offer & { lines: OfferLine[] }>(`/api/offers/${id}`),
		enabled: !!id,
	});
}

export function useCreateOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateOfferInput) => api.post<Offer>("/api/offers", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateOfferInput> }) =>
			api.patch<Offer>(`/api/offers/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/offers/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useSendOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/send`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useAcceptOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/accept`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export interface FoundDatasheet {
	description: string;
	path: string;
}

export interface OfferEmailPreview {
	email: string;
	cc: string;
	subject: string;
	message: string;
	foundDatasheets: FoundDatasheet[];
	missingDatasheets: string[];
	excludedDatasheets: string[];
	noNextcloud: boolean;
	signatureMissingReason: string | null;
}

export function useOfferEmailPreview() {
	return useMutation({
		mutationFn: (id: string) => api.get<OfferEmailPreview>(`/api/offers/${id}/email-preview`),
	});
}

export interface SendOfferEmailInput {
	id: string;
	email: string;
	cc?: string;
	subject: string;
	message: string;
	lang: "en" | "tr";
	variant?: "offer" | "proforma";
	bankName?: string;
	currency?: string;
	bankName2?: string;
	currency2?: string;
}

export function useSendOfferEmail() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }: SendOfferEmailInput) =>
			api.post<{
				sent: boolean;
				missingDatasheets: string[];
				offer: Offer;
				nextcloudSaveWarning: string | null;
			}>(`/api/offers/${id}/send-email`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useConvertToOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			isCustomDesign,
			alternativeGroup,
		}: {
			id: string;
			isCustomDesign: boolean;
			alternativeGroup?: number | undefined;
		}) =>
			api.post<{ id: string }>(`/api/offers/${id}/convert-to-order`, {
				isCustomDesign,
				...(alternativeGroup !== undefined ? { alternativeGroup } : {}),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: ["orders", "list"] });
		},
	});
}

export interface Offer {
	id: string;
	documentNumber: string | null;
	offerDate: string | null;
	validUntil: string | null;
	currency: string | null;
	exchangeRate: string | null;
	paymentTerms: string | null;
	deliveryTerms: string | null;
	notes: string | null;
	internalNotes: string | null;
	pdfNotes: string | null;
	commitmentDate: string | null;
	commitmentValue: string | null;
	commitmentUnit: string | null;
	offerOrderDeliveryType: string | null;
	termsCommissioning: string | null;
	termsAssembly: string | null;
	termsVat: string | null;
	termsQuality: string | null;
	termsValidity: string | null;
	termsWarranty: string | null;
	termsPayment: string | null;
	termsDelivery: string | null;
	termsI18n: Record<string, Record<string, string>> | null;
	startupDescription: string | null;
	installationDescription: string | null;
	tmsDescription: string | null;
	atsDescription: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	isTaxIncluded: boolean;
	subtotal: string | null;
	vatTotal: string | null;
	grossTotal: string | null;
	total: string | null;
	status:
		| "draft"
		| "sent"
		| "accepted"
		| "rejected"
		| "expired"
		| "cancelled"
		| "converted_to_order";
	sentAt: string | null;
	emailSentAt: string | null;
	versionNumber: number;
	isLatestVersion: boolean;
	parentOfferId: string | null;
	companyId: string | null;
	companyName: string | null;
	contactPersonId: string | null;
	opportunityId: string | null;
	assignedTo: string | null;
	createdAt: string;
	updatedAt: string;
}

interface OfferLine {
	id: string;
	lineNumber: number;
	itemCode: string | null;
	itemDescription: string | null;
	displayDescription: string | null;
	quantity: string;
	lineDetail: string | null;
	alternativeGroup: number | null;
	alternativeCurrency: string | null;
	unit: string | null;
	unitPrice: string;
	givenUnitPrice: string | null;
	vatRate: string | null;
	vatGroup: string | null;
	discountPercent: string | null;
	lineTotal: string | null;
	isFreeText: boolean;
	showInPdf: boolean;
	showPriceInPdf: boolean;
	showDiscountInPdf: boolean;
	sendDatasheetInEmail: boolean;
	notes: string | null;
	priceListModel: string | null;
	stockItemId: string | null;
}

interface CreateOfferInput {
	offerDate?: string | undefined;
	validUntil?: string | undefined;
	currency?: string | undefined;
	exchangeRate?: number | undefined;
	paymentTerms?: string | undefined;
	deliveryTerms?: string | undefined;
	notes?: string | undefined;
	internalNotes?: string | undefined;
	pdfNotes?: string | undefined;
	commitmentDate?: string | undefined;
	commitmentValue?: string | undefined;
	commitmentUnit?: string | undefined;
	offerOrderDeliveryType?: string | undefined;
	termsCommissioning?: string | undefined;
	termsAssembly?: string | undefined;
	termsVat?: string | undefined;
	termsQuality?: string | undefined;
	termsValidity?: string | undefined;
	termsWarranty?: string | undefined;
	termsPayment?: string | undefined;
	termsDelivery?: string | undefined;
	termsI18n?: Record<string, Record<string, string>> | undefined;
	startupDescription?: string | undefined;
	installationDescription?: string | undefined;
	tmsDescription?: string | undefined;
	atsDescription?: string | undefined;
	factorySpecialNotes?: string | undefined;
	generalNotes?: string | undefined;
	isTaxIncluded?: boolean | undefined;
	companyId?: string | undefined;
	contactPersonId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	lines: {
		lineNumber: number;
		itemCode?: string | undefined;
		itemDescription?: string | undefined;
		displayDescription?: string | undefined;
		quantity: number;
		lineDetail?: string | undefined;
		unit?: string | undefined;
		unitPrice: number;
		givenUnitPrice?: number | undefined;
		vatRate?: number | undefined;
		vatGroup?: string | undefined;
		discountPercent?: number | undefined;
		lineTotal?: number | undefined;
		notes?: string | undefined;
		priceListModel?: string | undefined;
		stockItemId?: string | undefined;
		isFreeText?: boolean | undefined;
		showInPdf?: boolean | undefined;
		showPriceInPdf?: boolean | undefined;
		showDiscountInPdf?: boolean | undefined;
		sendDatasheetInEmail?: boolean | undefined;
	}[];
}
